import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import If from "components/common/If";
import NoData from "components/common/NoData";
import Translate from "components/common/Translate";
import ModalPortal from "components/modal/ModalPortal";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { agricultureMaintenanceLogTypeDeleteMutation } from "types/agriculture/maintenance-log-types";
import { NotNull } from "types/common-types";
import { AgricultureMaintenanceLogTypeListDocument, AgricultureMaintenanceLogTypeListQuery, AgricultureMaintenanceLogTypeListQueryVariables, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import AgricultureMaintenanceTypeFormInputs from "./AgricultureMaintenanceTypeFormInputs";
import Button from "components/common/button/Button";

type AgricultureMaintenanceLogTypeListProps = {
    addNew?: boolean | string | AppRoutes;
    name: string;
}

const AgricultureMaintenanceLogTypeList = (props: AgricultureMaintenanceLogTypeListProps) => {
    const [state, setState] = useState<ComponentStateTypes>('loading');
    const [data, setData] = useState<NotNull<AgricultureMaintenanceLogTypeListQuery['maintenanceLogTypes']>['items']>([]);
    const context = useAppContext();
    const [searchParams] = useSearchParams();
    let modalPrefix = `agriculture-maintenance-log-type-list-${props.name}`;


    useEffect(() => {
        loadData().then();
    }, []);

    useEffect(() => {
        modalPrefix = `agriculture-maintenance-log-type-list-${props.name}`;
    }, [props.name]);

    const loadData = async () => {
        setState('loading');

        try {
            const response = await graph<AgricultureMaintenanceLogTypeListQuery, AgricultureMaintenanceLogTypeListQueryVariables>({
                query: AgricultureMaintenanceLogTypeListDocument,
                variables: {
                    order: [
                        {
                            name: SortEnumType.Asc
                        }
                    ]
                }
            });

            setState('');
            setData(response.data!.maintenanceLogTypes!.items);
        } catch (error) {
            setState('error');
            context.notify('error', 'error.loading-data')
            throw error;
        }
    };

    const onDelete = async () => {
        var id = Number(context.modal.getData(`${modalPrefix}-delete`));
        if(!id) {
            context.notify('error', 'error.invalid-data');
            return;
        }
        setState('loading');
        try {
            await agricultureMaintenanceLogTypeDeleteMutation({
                id
            });
            await loadData();
        } catch (error) {
            setState('');
        }
    };

    const renderList = () => {
        if (!data?.length) return <li><NoData /></li>;
        return data.map((item, index) => (
            <li key={item.id} className="border-b theme-border pb-2">
                <div className="flex">
                    <div className="w-full my-auto">
                        {item.name}
                    </div>
                    <If condition={context.userHasRole(Roles.AGRICULTURE_WORK_WRITE)}>
                        <div>
                            <div className="grid-cols-2 gap-4 grid text text-center">
                                <div className="p-2 my-auto text-center">
                                    <Button title="button.edit" className="link" onClick={()=> context.modal.open(`${modalPrefix}-form`, item)}>
                                        <FontAwesomeIcon icon={faPen} />
                                    </Button>
                                </div>
                                <div className="p-2 my-auto text-center">
                                    <Button title="button.delete" className="link text-danger" onClick={()=> context.modal.open(`${modalPrefix}-delete`, item.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </If>
                </div>
            </li>
        ))
    };

    return (
        <>
            <ComponentState state={state} >
                <ul>
                    <If condition={context.userHasRole(Roles.AGRICULTURE_SALES_WRITE) && props.addNew !== false}>
                        <li className="border-b theme-border pb-2">
                            <Button theme="link" title="" onClick={() => context.modal.open(`${modalPrefix}-form`)}>
                                <Translate i18nKey="button.add-new" />
                            </Button>
                        </li>
                    </If>
                    {renderList()}
                </ul>
            </ComponentState>
            <ModalPortal title="pages.agriculture.work.maintenance.type.form" name={`${modalPrefix}-form`}>
                <div>
                    {searchParams.has(`${modalPrefix}-form`) && <AgricultureMaintenanceTypeFormInputs
                        name="agriculture-maintenance-type-list-form-modal"
                        model={context.modal.getData(`${modalPrefix}-form`)}
                        onSubmit={() => {
                            loadData().then();
                            context.navigate(-1);
                        }}
                    />}
                </div>
            </ModalPortal>
            <ConfirmationModal text="common.delete?" 
                name={`${modalPrefix}-delete`}
                onConfirm={onDelete} 
            />
        </>
    );
};

export default AgricultureMaintenanceLogTypeList;
